import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DrawerContent } from 'components/common/drawer/DrawerContent';
import { RecipientField } from 'components/shared/sharing/RecipientField';
import {
  shareList,
  shareFile,
  shareFileRemove,
  resetShareList,
  listShareableAccounts,
  shareFileUpdateAccess,
} from 'store/slices/files';
import { PeopleWithAccess } from 'components/shared/sharing/PeopleWithAccess';

export const ShareQuoteSet = (props) => {
  const { close } = props;

  const dispatch = useDispatch();

  const selectedQuoteSet = useSelector((state) => state.marketplace.selectedQuoteSet);
  const currentUser = useSelector((state) => state.auth.currentUser);

  /* shareable accounts */
  const users = useSelector((state) => state.files.shareableAccounts.value);
  const usersLoading = useSelector((state) => state.files.shareableAccounts.loading);
  /* shared users */
  const sharedUserList = useSelector((state) => state.files.shareList.value);
  /* shareable accounts without auth user and already shared user */
  const userList = useMemo(() => {
    return users.filter((user) => {
      const isAuthUser = user.id === currentUser?.id;
      const isAlreadyShared = sharedUserList.some((sharedUser) => sharedUser?.owner_account?.id === user.id);
      return !isAuthUser && !isAlreadyShared;
    });
  }, [users, currentUser, sharedUserList]);

  useEffect(() => {
    dispatch(resetShareList());
    if (selectedQuoteSet?.id) {
      dispatch(shareList({ fileId: selectedQuoteSet?.id }));
    }
  }, [dispatch, selectedQuoteSet]);

  const handleShare = (user) => {
    dispatch(shareFile({ fileId: selectedQuoteSet.id, userId: user.id, access: 'Read' }))
      .unwrap()
      .then(() => setTimeout(() => dispatch(shareList({ fileId: selectedQuoteSet.id })), 1200));
  };

  const handleRemoveShare = (id) => {
    dispatch(shareFileRemove({ fileId: selectedQuoteSet.id, userId: id }))
      .unwrap()
      .then(() => dispatch(shareList({ fileId: selectedQuoteSet.id })));
  };

  const handleShareAccessUpdate = (id, access) => {
    dispatch(shareFileUpdateAccess({ fileId: selectedQuoteSet.id, userId: id, access }))
      .unwrap()
      .then(() => dispatch(shareList({ fileId: selectedQuoteSet.id })));
  };

  const handleSearch = (query) => {
    return dispatch(listShareableAccounts({ query }))
      .unwrap()
      .then((data) => data);
  };

  const handleUpdateShareFile = (userId, action) => {
    if (action === 'Remove') {
      handleRemoveShare(userId);
    } else {
      handleShareAccessUpdate(userId, action);
    }
  };

  return (
    <DrawerContent>
      <DrawerContent.Header title="Share Quote" />
      <DrawerContent.Wrapper>
        <DrawerContent.Content>
          <RecipientField
            optionsData={userList}
            backendSearch={handleSearch}
            loading={usersLoading}
            handleShare={handleShare}
          />
          <PeopleWithAccess>
            <PeopleWithAccess.Account>
              <PeopleWithAccess.AccountName accountName={currentUser.name} />
              <PeopleWithAccess.AccountAccess accountAccess="Owner" />
            </PeopleWithAccess.Account>
            {sharedUserList.map((user, index) => (
              <PeopleWithAccess.Account key={`${user.owner_account.id}-${index}`}>
                <PeopleWithAccess.AccountName accountName={user.owner_account.name} />
                <PeopleWithAccess.AccessOptions
                  value={user.access.includes('Write') ? 'Write' : 'Read'}
                  onChange={(access) => handleUpdateShareFile(user.owner_account.id, access)}
                />
              </PeopleWithAccess.Account>
            ))}
          </PeopleWithAccess>
        </DrawerContent.Content>
        <DrawerContent.CancelButton cancel={close} />
      </DrawerContent.Wrapper>
    </DrawerContent>
  );
};
